import React, { PureComponent } from 'react';
import { Div } from 'Common/components/Atoms/Div';
import { ContentLoader } from 'Common/components/Atoms/ContentLoader';
import { SkillSelector } from 'Common/components/Molecules/SkillSelector';
import { Text } from 'Common/components/Atoms/Text';
import { InputLabel } from '../ReviewerSignUpProfile';
import StepCard from '../../components/StepCard';
import { actions as signupsActions } from '../../reducers/signups';
import { actions as accountActions } from '../../reducers/Account';
import {
  accountUserSelector,
  isLoadingAccountSelector,
} from '../../selectors/Account';
import { connect } from 'react-redux';
import { Action, bindActionCreators, Dispatch } from 'redux';
import isEmpty from 'lodash/isEmpty';
import { User, Skill, ReviewerSkill } from '../../models/User';
import { SKILLS } from 'DisplayText/ReviewerOnboarding/Application/Skills';
import { logEvent, REVIEWER_APPLICATION_SKILLS } from 'utils/analytics';

export interface Props {
  user: User;
  skills: Skill[];
  fetchSkills: () => void;
  storeReviewerSkills: (skills: { [key: number]: ReviewerSkill }) => void;
  isUpdating: boolean;
  isLoadingAccount: boolean;
  previousStep: () => void;
}

interface State {
  errors: boolean;
  skills: Skill[];
}

interface SkillTag {
  id: number;
  value: number;
}

export class ReviewerProfileSkills extends PureComponent<Props, State> {
  state = {
    errors: false,
    skills: this.props.user.reviewer_profile?.skills || [],
  };

  componentDidUpdate(prevProps: Props) {
    if (!prevProps.user) {
      this.setInitialSkillsFromProfile();
    }
  }

  componentDidMount() {
    const { skills, fetchSkills } = this.props;
    this.setInitialSkillsFromProfile();
    if (isEmpty(skills)) {
      fetchSkills();
    }

    logEvent(REVIEWER_APPLICATION_SKILLS);
  }

  setInitialSkillsFromProfile = () => {
    const { user } = this.props;
    const { reviewer_profile } = user;
    const { skills } = reviewer_profile || {};
    if (skills && !isEmpty(skills)) {
      this.setState({
        skills,
      });
    }
  };

  getSkillOptions = (): { label: string; value: number }[] => {
    const { skills } = this.props;
    return skills.map((skill) => ({
      label: skill.name,
      value: skill.id,
    }));
  };

  getSkillValues = (): { label: string; value: number }[] => {
    const { skills } = this.state;

    return skills.map((skill) => ({
      label: skill.name,
      value: skill.id,
    }));
  };

  handleSkillUpdate = (skillTags: SkillTag[]) => {
    const { skills: skillOptions } = this.props;
    const { skills } = this.state;
    if (!skillTags) {
      this.setState({ skills: [] });
      return;
    }

    const skillsAndOptions = skillTags.map((skill) => ({
      option: skillOptions.find((s) => s.id === skill.value),
      existingSkill: skills.find((s) => s.id === skill.value),
    }));

    const filtered = skillsAndOptions.filter(
      ({ option }) => option !== undefined
    ) as { option: Skill; existingSkill: Skill }[];
    const newSkillsState = filtered.map(({ option, existingSkill }) => ({
      name: option.name,
      id: option.id,
      years_experience: existingSkill?.years_experience,
      skill_level: existingSkill?.skill_level,
    }));

    this.setState({ skills: newSkillsState, errors: false });
  };

  handleSubmit = () => {
    const { storeReviewerSkills } = this.props;
    const { skills } = this.state;
    if (isEmpty(skills)) {
      this.setState({ errors: true });
    } else {
      const skillsToSubmit: {
        [key: number]: ReviewerSkill;
      } = {};
      skills.forEach((skill) => {
        skillsToSubmit[skill.id] = {
          years_experience: skill.years_experience || null,
          skill_level: skill.skill_level || 1,
        };
      });

      storeReviewerSkills(skillsToSubmit);
    }
  };

  render() {
    const { isUpdating, isLoadingAccount, previousStep } = this.props;
    const { errors, skills } = this.state;
    const maximum = {
      length: 10,
      text: <Text variant="body1">{SKILLS.MAXIMUM_TEXT}</Text>,
    };

    return (
      <StepCard
        handleSubmit={this.handleSubmit}
        isUpdating={isUpdating}
        previousStep={previousStep}
      >
        <InputLabel>{SKILLS.TITLE}</InputLabel>
        {isLoadingAccount ? (
          <ContentLoader height="38px" width="100%" />
        ) : (
          <React.Fragment>
            <Div my={1.5} maxHeight={200}>
              <SkillSelector
                data-testid="qa-skills-select"
                key={JSON.stringify(skills)}
                name="skilltags"
                skills={this.getSkillOptions()}
                onChange={this.handleSkillUpdate}
                values={this.getSkillValues()}
                maximum={maximum}
              />
            </Div>
            {errors && (
              <Text variant="caption" px={2} py={0.5} color="error">
                {SKILLS.ERROR}
              </Text>
            )}
          </React.Fragment>
        )}
      </StepCard>
    );
  }
}

function mapStateToProps(state: any) {
  const { signups } = state;
  const user = accountUserSelector(state);
  const isLoadingAccount = isLoadingAccountSelector(state);
  return {
    isUpdating: signups.isUpdating,
    isLoadingAccount,
    user,
    skills: signups.skills,
    error: signups.error,
  };
}

function mapDispatchToProps(dispatch: Dispatch<Action>) {
  return bindActionCreators({ ...signupsActions, ...accountActions }, dispatch);
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ReviewerProfileSkills);
